import { AiBanner, Css } from "@homebound/beam";
import { ReactNode } from "react";
import { AiFieldProposalStatus } from "src/generated/graphqlTypes";

export type PlanPackageAiReviewBannerProps = {
  /** Renders nothing when false. */
  hasPending: boolean;
  /** Called with the decision for every pending proposal on the tab; return the save to show it loading. */
  onReconcileAll: (decision: AiFieldProposalStatus.Accepted | AiFieldProposalStatus.Rejected) => void | Promise<void>;
  /** Disables both actions, e.g. on a version the user can't edit; a `ReactNode` shows as their tooltip. */
  disabled?: boolean | ReactNode;
} & (
  | {
      /** How many proposals need attention before they can be applied, counted into the plan-level copy. */
      warnings: number;
      title?: never;
      message?: never;
    }
  | {
      /** Copy for a tab that isn't reviewing the plan itself, e.g. Takeoffs. */
      title: string;
      message: string;
      warnings?: never;
    }
);

/**
 * The Accept All / Ignore All banner shown above a plan package tab with AI proposals awaiting
 * review. Render it unwrapped, directly in the tab panel, so it spans edge to edge.
 */
export function PlanPackageAiReviewBanner(props: PlanPackageAiReviewBannerProps) {
  const { hasPending, onReconcileAll, disabled } = props;
  if (!hasPending) return null;
  const { title, message } =
    props.warnings === undefined
      ? props
      : {
          title: "Review changes to your plan found in your latest document capture.",
          message: `Review the changes highlighted below,${props.warnings > 0 ? ` including ${props.warnings} warning${props.warnings === 1 ? "" : "s"},` : ""} then accept or ignore them. Accept All and Ignore All apply only to this tab.`,
        };
  return (
    // TODO: Move this to the banner slot in the PageHeaderLayout once available.
    // https://linear.app/homebound-team/issue/DS-385/move-plan-package-ai-banner-into-banner-slot
    // Pulls up over the tab panel's top padding, then gives that gap back below the banner
    <div css={Css.mt(-3).mb3.$}>
      <AiBanner
        title={title}
        message={message}
        primaryAction={{ label: "Accept All", disabled, onClick: () => onReconcileAll(AiFieldProposalStatus.Accepted) }}
        secondaryAction={{
          label: "Ignore All",
          disabled,
          onClick: () => onReconcileAll(AiFieldProposalStatus.Rejected),
        }}
      />
    </div>
  );
}
